
import { MapPin, RefreshCw, Check, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

interface LocationDisplayProps {
  city: string | null;
  state: string | null;
  country: string | null;
  isLoading: boolean;
  hasError: boolean;
  isApproximate?: boolean;
  onRefresh: () => void;
}

const LocationDisplay = ({
  city,
  state,
  country,
  isLoading,
  hasError,
  isApproximate = false,
  onRefresh
}: LocationDisplayProps) => {
  if (!city && !country && !isLoading && !hasError) {
    return null;
  }

  return (
    <Card className="mt-3 border-amber-200 bg-amber-50">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {isLoading ? (
              <RefreshCw className="h-4 w-4 text-amber-600 animate-spin" />
            ) : hasError ? (
              <AlertCircle className="h-4 w-4 text-red-500" />
            ) : (
              <Check className="h-4 w-4 text-amber-600" />
            )}
            <MapPin className="h-4 w-4 text-amber-600" />
            <div>
              <p className="text-sm font-semibold text-amber-800">
                {isLoading ? "Detecting your location..." : isApproximate ? "Approximate Location" : "Your Location"}
              </p>
              {!isLoading && !hasError && (
                <div className="text-xs text-amber-700">
                  {city && state && country ? (
                    <span>{city}, {state}, {country}</span>
                  ) : city && country ? (
                    <span>{city}, {country}</span>
                  ) : country ? (
                    <span>{country}</span>
                  ) : (
                    <span>Location detected</span>
                  )}
                  {isApproximate && (
                    <span className="text-xs text-amber-600 ml-1">(based on IP)</span>
                  )}
                </div>
              )}
              {hasError && (
                <div className="text-xs text-red-600">
                  Unable to detect location
                </div>
              )}
            </div>
          </div>
          {!isLoading && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRefresh}
              className="h-8 px-2 text-amber-700 hover:text-amber-800 hover:bg-amber-100"
            >
              <RefreshCw className="h-3 w-3" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default LocationDisplay;
